import React, { useEffect, useMemo, useState } from 'react';
import { Table, Typography, Button, Select } from 'antd';
import { DownOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import './ProjectTable.scss';
import CheckboxModel from '../../CustomModel/CheckboxModel/CheckboxModel';
import VendorModel from '../../CustomModel/VendorModel/VendorModel';
import { useDispatch, useSelector } from 'react-redux';
import {
        addPackageBill,
        deletePackages,
        fetchUnitCommercialOptions,
        getBillingCost,
} from '../../../../redux/budgetSlice/budgetSlice';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
        generateMonthExecution,
        transformData,
} from '../../../../pages/BudgetSubmission/constants';
import { addProductBilling, fetchProductType } from '../../../../redux/unitSlice/unitSlice';
import Loader from '../../LoadingSpinner/LoadingSpinner';
import { computeTotalGrossBillingRow } from '../../../../utils/VerticalTotalOfTable';
import { generateColumns, parseDMY } from '../../../../utils/utilFunctions';
import { addMonths, differenceInMonths, format, parse } from 'date-fns';
import openNotification from '../../OpenNotification';
import { fetchPackages } from '../../../../redux/packageSlice/packageSlice';

const parseNum = (v) => {
        if (v === null || v === undefined) return 0;
        const n = Number(String(v).replace(/,/g, '').trim());
        return Number.isFinite(n) ? n : 0;
};

// Retained as a fallback for the very first render, before `systemDate` has
// loaded from Redux.
const getCurrentFYStart = () => {
        const today = new Date();
        return today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
};

// NEW: FY start now comes from the admin-configured System Start Date
// (Redux `otherProjects.systemDate`, format DD-MM-YYYY) instead of today's date.
const parseSystemFYStart = (startDate) => {
        if (!startDate) return null;

        const [day, month, year] = startDate.split("-").map(Number);

        if (!day || !month || !year) return null;

        return month >= 4 ? year : year - 1; // Apr-Mar FY cycle
};

// OLD: BILLED_UPTO was rendered straight off the API's static field, and the
// "Billed Upto" title/columns were anchored to this project's own UPTO_DATE.
// NEW: Billed Upto = the API's baseline BILLED_UPTO plus every FY's
// "_formatted" value ("YYYY-YYYY_formatted") that falls strictly before the
// current (system-date-derived) FY - same "dynamic upto" rule used across
// the other reports/tables. Only applied per-row - the Total row's
// BILLED_UPTO/TCR/BILLED_BALANCE deliberately come from billingCostCompleteData
// (a separate backend aggregate, see computeTotalGrossBillingRow) for
// discrepancy-checking, so it's left untouched here.
const getDynamicUpto = (row = {}, fyStart, baseField) => {
        let sum = parseNum(row[baseField]);

        Object.keys(row).forEach((key) => {
                const match = /^(\d{4})-\d{4}_formatted$/.exec(key);
                if (!match) return;

                const fyStartYear = Number(match[1]);
                if (fyStartYear < fyStart) {
                        sum += parseNum(row[key]);
                }
        });

        return sum.toFixed(2);
};

const ProjectBillingTable = ({ addPackageButton = true, version = "C" }) => {
        const { Text } = Typography;
        const { user } = useSelector((state) => state.auth);
        const {
                projectDates,
                billingCost,
                billingCostVersion,
                unitCommercialOption,
                loading,
                billingCostCompleteData,
                billingCostCompleteDataVersion
        } = useSelector((state) => state.budget);
        const { systemDate } = useSelector((state) => state.otherProjects);
        const { productType, productListingData } = useSelector((state) => state.unit);
        const [searchParams] = useSearchParams();
        const unitType = searchParams?.get('unitType')?.toUpperCase();
        const loginType = unitType ?? user?.role;
        const dispatch = useDispatch();
        const location = useLocation();
        const [discrepancyNotified, setDiscrepancyNotified] = useState(false);
        const ioNo = location.state?.ioNo || '--';
        const revisedNo = location.state?.rvNo || '--';
        const [productOptions, setProductOptions] = useState([]);
        const formattedData = unitCommercialOption?.map((item) => ({
                label: item.COMMERCIAL_NAME,
                value: item.COMMERCIAL_CODE,
        }));
        const productTypeOptions = productType?.map((item) => ({
                value: item.PRODUCT_TYPE_CODE,
                label: item.PRODUCT_TYPE,
        }));

        const [selectedPackages, setSelectedPackages] = useState([]);
        const [unitCommercial, setUnitCommercial] = useState();
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [selectedCategory, setSelectedCategory] = useState(null); // Track clicked section
        const [headtType, setHeadtType] = useState(null);
        const [dropdownState, setDropdownState] = useState({});

        // OLD: const CURRENT_FY_START = getCurrentFYStart(); (fixed once at import time)
        // NEW: derive it from the admin-configured System Start Date, falling back
        // to today's date until systemDate has loaded.
        const CURRENT_FY_START = useMemo(
                () => parseSystemFYStart(systemDate?.STARTDATE) ?? getCurrentFYStart(),
                [systemDate]
        );

        // OLD: title was anchored to this project's own UPTO_DATE.
        // const formattedUptoDate = projectDates?.UPTO_DATE
        //         ? format(parse(projectDates.UPTO_DATE, 'dd-MM-yyyy', new Date()), "MMM''yy")
        //         : '';
        const zeroDateParsed = parseDMY(projectDates?.ZERO_DATE);
        const uptoDateParsed = parseDMY(projectDates?.UPTO_DATE);
        const expectedDateParsed = parseDMY(projectDates?.EXPECTED_DATE);
        const unitPlanDateParsed = parseDMY(projectDates?.UNIT_BILLING_DATE);
        let startMonth = 4;
        let startYear = 2025;
        const role = sessionStorage.getItem('role');

        // OLD: rendered columns started the month after this project's own
        // UPTO_DATE.
        // NEW: anchored to the system-date-derived current FY (April of
        // CURRENT_FY_START) instead - consistent with the dynamic Billed Upto value.
        // if (uptoDateParsed) {
        //         const startMonthDate = addMonths(uptoDateParsed, 1); // ✅ Move to next month
        //         startMonth = startMonthDate.getMonth() + 1; // 1-based month
        //         startYear = startMonthDate.getFullYear();
        // }
        startMonth = 4;
        startYear = CURRENT_FY_START;
        // const endYear = expectedDateParsed ? expectedDateParsed.getFullYear() : 2026;
        // const endMonth = expectedDateParsed ? expectedDateParsed.getMonth() + 1 : 12;
        // const endMonth = 12;
        const sourceData =
                version === "C"
                        ? billingCost
                        : billingCostVersion;
        const monthMap = {
                Jan: 1, Feb: 2, Mar: 3, Apr: 4,
                May: 5, Jun: 6, Jul: 7, Aug: 8,
                Sep: 9, Oct: 10, Nov: 11, Dec: 12,
        };

        let latestYear = startYear;
        let latestMonth = 12;

        const monthKeys = sourceData.flatMap(row =>
                Object.keys(row).filter(key => /^[A-Z][a-z]{2}_\d{2}$/.test(key))
        );

        if (monthKeys.length > 0) {
                let maxValue = 0;

                monthKeys.forEach(key => {
                        const [mon, yy] = key.split("_");
                        const fullYear = 2000 + Number(yy);
                        const monthNo = monthMap[mon];

                        const value = fullYear * 100 + monthNo;

                        if (value > maxValue) {
                                maxValue = value;
                                latestYear = fullYear;
                                latestMonth = monthNo;
                        }
                });
        }

        const endYear = latestYear;
        const endMonth = latestMonth;

        let maxDataEndYear = 0;

        // const sourceData =
        //         version === "C"
        //                 ? billingCost
        //                 : billingCostVersion;

        if (Array.isArray(sourceData) && sourceData.length > 0) {
                // const firstRow = sourceData[0];

                // const fyKeys = Object.keys(firstRow).filter(
                //         (key) => /^\d{4}-\d{4}$/.test(key)
                // );
                const fyKeys = sourceData.flatMap(row =>
                        Object.keys(row).filter(key => /^\d{4}-\d{4}$/.test(key))
                );

                if (fyKeys.length > 0) {
                        maxDataEndYear = Math.max(
                                ...fyKeys.map((fy) => Number(fy.split("-")[1]))
                        );
                }
        }

        const expectedEndYear = expectedDateParsed
                ? expectedDateParsed.getFullYear()
                : 0;

        const unitPlanEndYear = unitPlanDateParsed
                ? unitPlanDateParsed.getFullYear()
                : 0;

        // Get the higher year between EXPECTED_DATE and UNIT_PLAN_DATE
        const higherDateYear = Math.max(expectedEndYear, unitPlanEndYear);

        // Use the higher year as final end year
        const finalEndYear = higherDateYear;

        // Dynamic end month based on higher date
        // const higherEndDate = expectedDateParsed && unitPlanDateParsed
        //         ? (expectedDateParsed > unitPlanDateParsed ? expectedDateParsed : unitPlanDateParsed)
        //         : (expectedDateParsed || unitPlanDateParsed);

        // const endMonth = higherEndDate ? higherEndDate.getMonth() + 1 : 12;

        // const endYear = finalEndYear;

        // const endYear = maxDataEndYear
        //         ? maxDataEndYear - 1   // FY logic
        //         : startYear + 1;

        const startMonthNumber =
                zeroDateParsed && uptoDateParsed
                        ? differenceInMonths(uptoDateParsed, zeroDateParsed) + 3
                        : 0;

        // const totalMonth =
        //         zeroDateParsed && higherEndDate
        //                 ? differenceInMonths(higherEndDate, zeroDateParsed) + 3
        //                 : 0;

        const totalMonth =
                zeroDateParsed && maxDataEndYear
                        ? differenceInMonths(
                                new Date(maxDataEndYear, 11), // Dec of last FY
                                zeroDateParsed
                        ) + 3
                        : 0;

        const calculateBilledUpto = (row) => {
                if (!row) return 0;

                return Object.keys(row)
                        .filter((key) => key.startsWith('M_')) // month columns
                        .slice(0, startMonthNumber)
                        .reduce((sum, key) => sum + Number(row[key] || ""), "");
        };

        const calculateTotalBilledUpto = () => {
                return data
                        .filter(
                                (row) =>
                                        !row.boldHeading &&
                                        row.DESC !== 'Gross Billing Total'
                        )
                        .reduce((sum, row) => sum + calculateBilledUpto(row), "");
        };


        const handleDeletePackages = (record) => {
                const commonParams = {
                        IO_NO: record?.IO_NO,
                        REV_NO: record?.REV_NO,
                        HEAD_TYPE: record?.HEAD_TYPE,
                        type: 'B',
                        COMMERCIAL_CODE: record?.COMMERCIAL_CODE,
                };

                const deleteParams =
                        loginType === 'UNIT'
                                ? {
                                        ...commonParams,
                                        PRODUCT_CODE: record?.PRODUCT_CODE,
                                        PRODUCT_TYPE_CODE: record?.PRODUCT_TYPE_CODE,
                                }
                                : {
                                        ...commonParams,
                                        PACKAGE_ID: record?.PACKAGE_ID,
                                };

                dispatch(deletePackages(deleteParams)).then(() => {
                        dispatch(getBillingCost({ IO_NO: ioNo, REV_NO: revisedNo }));
                });
        };

        let columns = [
                {
                        title: 'Description',
                        dataIndex: 'description',
                        key: 'description',
                        children: [
                                {
                                        title: 'Head',
                                        dataIndex: 'DESC',
                                        key: 'DESC',
                                        fixed: 'left',
                                        width: 100,
                                        align: 'center',
                                        render: (text, record, index) => {
                                                if (record.ADD_PACKAGE_STATUS === 1) {
                                                        return {
                                                                children: (
                                                                        <div className="w-full">
                                                                                <Button
                                                                                        type="icon"
                                                                                        size="small"
                                                                                        icon={
                                                                                                <DownOutlined />
                                                                                        }
                                                                                        onClick={() =>
                                                                                                toggleDropdown(
                                                                                                        record.head,
                                                                                                )
                                                                                        }
                                                                                        className={
                                                                                                'custom_button'
                                                                                        }
                                                                                />
                                                                                <Text>{text}</Text>{' '}
                                                                                &nbsp;
                                                                                {addPackageButton &&
                                                                                        loginType ===
                                                                                        'REGION' && (
                                                                                                <Button
                                                                                                        type="link"
                                                                                                        icon={
                                                                                                                <PlusOutlined />
                                                                                                        }
                                                                                                        onClick={() => {
                                                                                                                const headType =
                                                                                                                        record?.HEAD_TYPE ||
                                                                                                                        record?.CODE;
                                                                                                                dispatch(
                                                                                                                        fetchPackages(
                                                                                                                                {
                                                                                                                                        headType,
                                                                                                                                },
                                                                                                                        ),
                                                                                                                );
                                                                                                                handleAddPackage(
                                                                                                                        record?.HEAD_TYPE ||
                                                                                                                        record?.CODE,
                                                                                                                );
                                                                                                        }}
                                                                                                >
                                                                                                        Add
                                                                                                        Package
                                                                                                </Button>
                                                                                        )}
                                                                                {addPackageButton &&
                                                                                        record.ADD_PACKAGE_STATUS ===
                                                                                        1 &&
                                                                                        loginType ===
                                                                                        'UNIT' && (
                                                                                                <Button
                                                                                                        type="link"
                                                                                                        icon={
                                                                                                                <PlusOutlined />
                                                                                                        }
                                                                                                        onClick={() => {
                                                                                                                dispatch(
                                                                                                                        fetchProductType(
                                                                                                                                {
                                                                                                                                        headType:
                                                                                                                                                record?.HEAD_TYPE ||
                                                                                                                                                record?.CODE,
                                                                                                                                        projectType:
                                                                                                                                                'B',
                                                                                                                                },
                                                                                                                        ),
                                                                                                                );
                                                                                                                handleAddProduct(
                                                                                                                        record?.HEAD_TYPE ||
                                                                                                                        record.CODE,
                                                                                                                );
                                                                                                        }}
                                                                                                >
                                                                                                        Add
                                                                                                        Product
                                                                                                </Button>
                                                                                        )}
                                                                        </div>
                                                                ),
                                                                // props: { colSpan: 4 },
                                                        };
                                                }

                                                if (record.boldHeading) {
                                                        return (
                                                                <>
                                                                        <Text strong>{text}</Text>{' '}
                                                                        &nbsp;
                                                                </>
                                                        );
                                                }

                                                return <div className="w-[200px]">{text}</div>;
                                        },
                                },
                                {
                                        title: 'Unit Commercial',
                                        dataIndex: 'COMMERCIAL_NAME',
                                        key: 'COMMERCIAL_NAME',
                                        fixed: 'left',
                                        width: 150,
                                        align: 'center',
                                },
                                {
                                        title:
                                                loginType === 'UNIT'
                                                        ? 'Product Type'
                                                        : 'Package Type',
                                        dataIndex:
                                                loginType === 'UNIT'
                                                        ? 'PRODUCT_TYPE'
                                                        : 'PACKAGE_TYPE',
                                        key: loginType === 'UNIT' ? 'PRODUCT_TYPE' : 'PACKAGE_TYPE',
                                        fixed: 'left',
                                        width: 150,
                                        align: 'center',
                                },
                                {
                                        title:
                                                loginType === 'UNIT'
                                                        ? 'Product Name'
                                                        : 'Package Name',
                                        dataIndex:
                                                loginType === 'UNIT'
                                                        ? 'PRODUCT_NAME'
                                                        : 'PACKAGE_NAME',
                                        key: loginType === 'UNIT' ? 'PRODUCT_NAME' : 'PACKAGE_NAME',
                                        fixed: 'left',
                                        width: 150,
                                        align: 'center',
                                        render: (text, record) => {
                                                return (
                                                        <div className="flex justify-between w-full">
                                                                <div className="flex items-center justify-center w-[90%]">
                                                                        {text}
                                                                </div>
                                                                {(record.PACKAGE_NAME ||
                                                                        record.PRODUCT_NAME) &&
                                                                        addPackageButton && (
                                                                                <Button
                                                                                        type="text"
                                                                                        icon={
                                                                                                <DeleteOutlined
                                                                                                        style={{
                                                                                                                color: '#8c8c8c',
                                                                                                        }}
                                                                                                />
                                                                                        }
                                                                                        onClick={() =>
                                                                                                handleDeletePackages(
                                                                                                        record,
                                                                                                )
                                                                                        }
                                                                                        className="w-[10%]"
                                                                                />
                                                                        )}
                                                        </div>
                                                );
                                        },
                                },
                        ],
                },
                {
                        title: 'TCR',
                        dataIndex: 'TCR',
                        key: 'TCR',
                        width: 150,
                        align: 'center',
                        render: (text, record) => {
                                const isDiscrepant = record.TCR_discrepancy; // Check for discrepancy using new key
                                return (
                                        <span
                                                className="block text-right"
                                                // style={{ color: isDiscrepant ? 'red' : 'inherit' }}
                                                style={{ color: isDiscrepant ? 'inherit' : 'inherit' }}
                                        >
                                                {text}
                                        </span>
                                );
                        },
                },
                {
                        // OLD: title: `Billed Upto ${formattedUptoDate}`, (anchored to this project's own UPTO_DATE)
                        // NEW: anchored to the system-date-derived current FY.
                        title: `Billed Upto Mar'${String(CURRENT_FY_START).slice(-2)}`,
                        dataIndex: 'BILLED_UPTO',
                        key: 'BILLED_UPTO',
                        width: 150,
                        align: 'center',
                        // render: (_, record) => {
                        //         const value =
                        //                 record.DESC === 'Gross Billing Total'
                        //                         ? calculateTotalBilledUpto()
                        //                         : calculateBilledUpto(record);

                        //         return (
                        //                 <span className="block text-right">
                        //                         {value}
                        //                 </span>
                        //         );
                        // },
                        render: (text, record) => {
                                return (
                                        <span className="block text-right">
                                                {record.BILLED_UPTO || '--'}
                                        </span>
                                );
                        }

                },
                {
                        title: 'Balance to be billed',
                        dataIndex: 'BILLED_BALANCE',
                        key: 'BILLED_BALANCE',
                        width: 150,
                        align: 'center',
                        render: (text, record) => {
                                const billedUpto =
                                        record.DESC === 'Gross Billing Total'
                                                ? calculateTotalBilledUpto()
                                                : calculateBilledUpto(record);

                                // const tcr =
                                //         record.DESC === 'Gross Billing Total'
                                //                 ? data
                                //                         .filter(
                                //                                 (row) =>
                                //                                         !row.boldHeading &&
                                //                                         row.DESC !== 'Gross Billing Total'
                                //                         )
                                //                         .reduce(
                                //                                 (sum, row) =>
                                //                                         sum + Number(row.TCR || ""),
                                //                                 0
                                //                         )
                                //                 : Number(record.TCR || 0);

                                return (
                                        <span className="block text-right">
                                                {text || '--'}
                                                {/* {(tcr - billedUpto) === 0 ? "" : (tcr - billedUpto).toLocaleString('en-IN')} */}
                                        </span>
                                );
                        },

                },
        ];

        const [data, setData] = useState([]);

        const handleAddPackage = (category) => {
                setSelectedCategory(category);
                if (category) {
                        setHeadtType(category);
                }
                setIsModalOpen(true);
        };

        const handleAddProduct = (category) => {
                setSelectedCategory(category);
                if (category) {
                        setHeadtType(category);
                }
                setIsModalOpen(true);
        };
        const toggleDropdown = (category) => {
                setDropdownState((prev) => ({
                        ...prev,
                        [category]: !prev[category],
                }));
        };

        useEffect(() => {
                const productOptions = productListingData?.map((item) => ({
                        code: item.UNIT_CODE,
                        description: item.PRODUCT_NAME,
                        typeCode: item.PRODUCT_CODE,
                        type: item.UNIT_NAME,
                }));
                setProductOptions(productOptions);
        }, [productListingData]);

        useEffect(() => {
                if (selectedPackages.length === 0 || !selectedCategory) return;
                setSelectedCategory(null); // Reset category
        }, [selectedPackages, selectedCategory]);

        useEffect(() => {

                if (billingCost || billingCostVersion) {
                        // const totalRow = computeTotalGrossBillingRow(
                        //         billingCost,
                        //         'Gross Billing Total',
                        //         version === "C" ? billingCostCompleteData : billingCostCompleteDataVersion,
                        // );

                        const selectedData =
                                version === "C"
                                        ? billingCost
                                        : billingCostVersion;

                        // OLD: transformData/computeTotalGrossBillingRow read BILLED_UPTO
                        // straight off the API's static field.
                        // NEW: replace it with the dynamic value on every row first. Note:
                        // computeTotalGrossBillingRow's Total row still sources its own
                        // BILLED_UPTO/TCR/BILLED_BALANCE from billingCostCompleteData (a
                        // separate backend aggregate, for discrepancy-checking) rather than
                        // summing rows, so this override doesn't affect that row.
                        const selectedDataWithDynamicUpto = selectedData.map((row) => ({
                                ...row,
                                BILLED_UPTO: getDynamicUpto(row, CURRENT_FY_START, "BILLED_UPTO"),
                        }));

                        const transformedData = transformData({
                                data: selectedDataWithDynamicUpto,
                                dispatch,
                        });

                        const totalRow = computeTotalGrossBillingRow(
                                selectedDataWithDynamicUpto,
                                'Gross Billing Total',
                                version === "C"
                                        ? billingCostCompleteData
                                        : billingCostCompleteDataVersion,
                        );
                        delete totalRow.PACKAGE_NAME;
                        delete totalRow.PRODUCT_NAME;
                        delete totalRow.PRODUCT_TYPE;
                        delete totalRow.COMMERCIAL_NAME;
                        delete totalRow.PACKAGE_TYPE;

                        // Show notification only once
                        // if (!discrepancyNotified) {
                        //         const mismatchKeys = Object.keys(totalRow).filter((key) =>
                        //                 key.includes('_discrepancy'),
                        //         );
                        //         if (mismatchKeys.length > 0) {
                        //                 openNotification(
                        //                         'warning',
                        //                         'Mismatch Found',
                        //                         `Discrepancy found for: ${mismatchKeys
                        //                                 .map((k) => k.replace('_discrepancy', ''))
                        //                                 .join(', ')}`,
                        //                 );
                        //         }

                        //         setDiscrepancyNotified(true);
                        // }

                        if ('PACKAGE_NAME' in totalRow || 'PRODUCT_NAME' in totalRow) {
                                delete totalRow.PACKAGE_NAME;
                                delete totalRow.PRODUCT_NAME;
                        }

                        setData([
                                {
                                        key: '1',
                                        DESC: 'Month of Execution',
                                        ...generateMonthExecution(
                                                startMonthNumber,
                                                startYear,
                                                startMonth,
                                                totalMonth,
                                        ),
                                        boldHeading: true,
                                },
                                totalRow,
                                ...transformedData.map((item, index) => ({
                                        key: `billing-${index + 3}`,
                                        ...item,
                                })),
                        ]);
                }
        }, [billingCost, version, CURRENT_FY_START]);

        useEffect(() => {
                if (role !== 'ADMIN' && role !== 'SUPER_ADMIN') {

                        dispatch(getBillingCost({ IO_NO: ioNo, REV_NO: revisedNo }));
                        dispatch(fetchProductType());
                        dispatch(fetchUnitCommercialOptions());
                }
        }, [dispatch]);

        useEffect(() => { }, []);

        return (
                <>
                        {loading ? (
                                <Loader />
                        ) : (
                                <Table
                                        columns={generateColumns(
                                                startYear,
                                                endYear,
                                                startMonth,
                                                endMonth,
                                                columns,
                                                null
                                        )}
                                        dataSource={data.filter((item) => {
                                                if (!item.isPackage) return true; // Keep normal rows
                                                return dropdownState[item.package_row] || false; // Show/hide package rows dynamically
                                        })}
                                        pagination={false}
                                        bordered
                                        scroll={{ x: 'max-content', y: 400 }}
                                        className="custom-ant-table"
                                        rowClassName={(record, index) => {
                                                if (index === 1) {
                                                        return 'table-row-light-blue';
                                                } else if (index === 0) {
                                                        return 'table-row-dark';
                                                } else {
                                                        return 'table-row-light';
                                                }
                                        }}
                                />
                        )}
                        <div className="p-4">
                                {isModalOpen && (
                                        <>
                                                {loginType === 'UNIT' && (
                                                        <VendorModel
                                                                isOpen={loginType === 'UNIT'}
                                                                onClose={() =>
                                                                        setIsModalOpen(false)
                                                                }
                                                                title="IO Head"
                                                                initialValues={{
                                                                        packageTypes:
                                                                                selectedPackages,
                                                                        unitCommercial:
                                                                                unitCommercial,
                                                                }}
                                                                onSubmit={(values) => {
                                                                        setSelectedPackages(
                                                                                values?.packageTypes,
                                                                        );
                                                                        setUnitCommercial(
                                                                                values?.unitCommercial,
                                                                        );
                                                                        dispatch(
                                                                                addProductBilling({
                                                                                        IO_NO: ioNo,
                                                                                        REV_NO: revisedNo,
                                                                                        HEAD_TYPE:
                                                                                                headtType ||
                                                                                                '0',
                                                                                        COMMERCIAL_CODE:
                                                                                                values?.unitCommercial,
                                                                                        PRODUCT_CODE:
                                                                                                values?.packageTypes?.map(
                                                                                                        (
                                                                                                                pkg,
                                                                                                        ) =>
                                                                                                                pkg.typeCode,
                                                                                                ) ||
                                                                                                [],
                                                                                        PRODUCT_TYPE_CODE:
                                                                                                values?.productTypeCode,
                                                                                }),
                                                                        );
                                                                        setSelectedPackages([]);
                                                                        setUnitCommercial(null);
                                                                }}
                                                                labelHeading="Product"
                                                                placeholder="Search"
                                                                supplyOptions={productTypeOptions}
                                                                packageOptions={
                                                                        productOptions ?? []
                                                                }
                                                                options={formattedData}
                                                        />
                                                )}
                                                {loginType === 'REGION' && (
                                                        <CheckboxModel
                                                                isOpen={loginType !== 'UNIT'}
                                                                onClose={() =>
                                                                        setIsModalOpen(false)
                                                                }
                                                                title="Select Packages"
                                                                initialValues={{ packageTypes: [] }}
                                                                onSubmit={(values) => {
                                                                        setSelectedPackages(
                                                                                values?.packageTypes,
                                                                        );
                                                                        setUnitCommercial(
                                                                                values?.unitCommercial,
                                                                        );
                                                                        if (headtType) {
                                                                                dispatch(
                                                                                        addPackageBill(
                                                                                                {
                                                                                                        IO_NO: ioNo,
                                                                                                        REV_NO: revisedNo,
                                                                                                        HEAD_TYPE: headtType,
                                                                                                        COMMERCIAL_CODE:
                                                                                                                values?.unitCommercial,
                                                                                                        PACKAGE_ID:
                                                                                                                values?.packageTypes?.map(
                                                                                                                        (
                                                                                                                                pkg,
                                                                                                                        ) =>
                                                                                                                                pkg.PACKAGE_ID,
                                                                                                                ) ||
                                                                                                                [],
                                                                                                },
                                                                                        ),
                                                                                );
                                                                        }
                                                                }}
                                                                labelHeading={'Customer Package'}
                                                                placeholder={'Search'}
                                                                options={formattedData}
                                                        />
                                                )}
                                        </>
                                )}
                        </div>
                </>
        );
};

export default ProjectBillingTable;
